import { Link } from "react-router-dom";
import { Car, MapPin } from "lucide-react";
import JsonLd from "./JsonLd";
import { Button } from "@/components/ui/button";

const ORIGINS = [
  { city: "Austin", time: "45 min", distance: "45 mi", neighborhoods: "Round Rock, Cedar Park, South Austin, Buda, Kyle" },
  { city: "San Antonio", time: "50 min", distance: "55 mi", neighborhoods: "Stone Oak, Alamo Heights, Schertz, Cibolo, New Braunfels" },
  { city: "Houston", time: "2.5 hours", distance: "180 mi", neighborhoods: "Katy, Sugar Land, The Woodlands, Cypress, Memorial" },
  { city: "Dallas / Fort Worth", time: "4 hr", distance: "240 mi", neighborhoods: "Plano, Frisco, Arlington, Mansfield, Southlake" },
  { city: "San Marcos", time: "15 min", distance: "12 mi", neighborhoods: "Texas State, Wonder World, IH-35 corridor" },
  { city: "New Braunfels", time: "20 min", distance: "18 mi", neighborhoods: "Gruene, Canyon Lake, Schlitterbahn area" },
];

const placeSchema = {
  "@context": "https://schema.org",
  "@type": "Place",
  name: "Son's Blue River Camp",
  address: {
    "@type": "PostalAddress",
    streetAddress: "2769 Sherrill Rd",
    addressLocality: "Kingsbury",
    addressRegion: "TX",
    postalCode: "78638",
    addressCountry: "US",
  },
  geo: { "@type": "GeoCoordinates", latitude: 29.72751, longitude: -97.76306 },
  containedInPlace: { "@type": "AdministrativeArea", name: "Guadalupe County, Texas" },
};

const DriveTimesSection = () => {
  return (
    <section className="py-16 bg-background border-y border-border/40">
      <JsonLd data={placeSchema} />
      <div className="container mx-auto px-4 max-w-6xl">
        <div className="max-w-3xl mx-auto text-center mb-10">
          <div className="inline-flex items-center gap-2 text-river text-sm font-cta uppercase tracking-wider mb-3">
            <MapPin className="h-4 w-4" /> Getting Here
          </div>
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground mb-4">
            Drive Times to Son's Blue River Camp
          </h2>
          <p className="text-muted-foreground text-lg">
            We're in Kingsbury, Texas — between San Marcos and Seguin on the spring-fed San Marcos River. An easy drive from every major Central Texas hub.
          </p>
        </div>

        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {ORIGINS.map((o) => (
            <div key={o.city} className="rounded-lg border border-border bg-card p-5 shadow-soft">
              <div className="flex items-start justify-between gap-3 mb-2">
                <div className="flex items-center gap-2">
                  <Car className="h-5 w-5 text-river" />
                  <h3 className="font-heading text-lg font-semibold text-foreground">From {o.city}</h3>
                </div>
                <div className="text-right">
                  <div className="font-cta font-bold text-river">{o.time}</div>
                  <div className="text-xs text-muted-foreground">{o.distance}</div>
                </div>
              </div>
              <p className="text-sm text-muted-foreground leading-relaxed">{o.neighborhoods}</p>
            </div>
          ))}
        </div>

        <div className="mt-12 max-w-3xl mx-auto text-center">
          <h3 className="font-heading text-2xl md:text-3xl font-bold text-foreground mb-4">
            Coming from the City?
          </h3>
          <p className="text-muted-foreground text-lg mb-6">
            Son's Blue River Camp sits on 8 private acres of the spring-fed San Marcos River in Kingsbury, TX — a quieter, family-friendly float than the Comal or Guadalupe. We're about 50 minutes from San Antonio, 45 minutes from Austin, and 2.5 hours west of Houston.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Button variant="river" asChild>
              <Link to="/river-cabins-san-antonio">River Cabins — San Antonio</Link>
            </Button>
            <Button variant="river" asChild>
              <Link to="/river-cabins-austin">River Cabins — Austin</Link>
            </Button>
            <Button variant="river" asChild>
              <Link to="/river-cabins-houston">River Cabins — Houston</Link>
            </Button>
          </div>
        </div>

        <div className="mt-8 text-center">
          <Link to="/contact" className="text-river font-cta font-semibold hover:underline">
            Get full directions →
          </Link>
        </div>
      </div>
    </section>
  );
};

export default DriveTimesSection;